import React from 'react';
import { Flex, message, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FEDEX_PICKUP_CHANGED_EVENT } from '@/common/zoomlogiUiEvents';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToken } from '../../common/helper';
import { CustomModalWithoutButton } from '../../components';
import ShareShipment from '../Modals/ShareShipment';
import CoC_CT from './partials/CoC_CT';
import type { CocCommentCreatedMeta } from './partials/ReplyCommentBox';
import {
  AIWorkFlow,
  ShipmentHeader,
  ArchiveShipmentModal,
  ShipmentAlerts,
  ShipmentDetail,
} from './components';
import './Shipment.scss';
import {
  trackExecutionLegViewed,
  trackShipmentViewed,
  useFetchShipmentAlerts,
  useFetchSingleShipment,
  useFetchExecutionLegDetail,
  useUnAuthorize,
} from '../../hooks';
import { isZoomlogiSupport } from '@/common/utils/utils';
import { canQuoteOrBookShell } from './shellShipmentHelpers';
import {
  ShipmentAlertData,
  SingleShipmentDataExtended,
  ExecutionLegDetail,
  LatLng,
} from '@/apis/types';
import MarkAsDeliveredModal from './ControlTower/MarkAsDeliveredModal';
import { useScrollToCocComment } from './ControlTower/hooks/useScrollToCocComment';

const ALERT_POLL_INTERVAL_MS = 30000;
const DETAIL_FORCE_REFRESH_MS = 120000;

export interface DrawerNavEntry {
  id: string;
  legType: 'CL' | 'EL';
  reference?: string;
}

interface ShipmentDetailDrawerWithTeamsProps {
  drawerAlert?: {
    CoC_ID?: string | null;
    scrollToCommentsBottom?: boolean;
    initialAction?: string;
    /** Breadcrumb stack of panels that were opened to reach the current one
     *  (oldest first). Used to render the back-navigation header and to let
     *  nested leg clicks push another entry before navigating deeper. */
    navStack?: DrawerNavEntry[];
  } | null;
  isOpen: boolean;
  toggleDrawer: (
    action: string | boolean,
    type?: string,
    id?: string,
    alert?: unknown,
    legType?: 'CL' | 'EL'
  ) => void;
  openShipmentId: string;
  shipmentSuccess: () => void;
  filterByLinkCode: (linkCode: string) => void;
  isFromHome?: boolean;
  isFromActivityFeed?: boolean; //Users Activity "back to User activity" arrow in the header
  onLinkedShipmentClick?: (shipmentId: string) => void;
  /** When set, shell shipments render a Quote button in the header that
   *  invokes this callback with the loaded shipment. */
  onQuoteFromShipment?: (shipment: SingleShipmentDataExtended) => void;
  /** When set, shell shipments render a Book button in the header that
   *  invokes this callback with the loaded shipment. */
  onBookFromShipment?: (shipment: SingleShipmentDataExtended) => void;
  /** FedEx return label — opens Quote drawer in return mode for this shipment. */
  onGenerateReturnLabel?: (shipment: SingleShipmentDataExtended) => void;
  /** Host renders the SchedulePickup drawer panel (Shipments page only). */
  supportsSchedulePickup?: boolean;
  onCocCommentCreated?: (
    shipmentId: string,
    comment: CocCommentCreatedMeta
  ) => void;
  onShipmentCommentsRead?: (shipmentId: string) => void;
  /** When this number changes (parent bumps it), the drawer refetches its
   *  shipment data — used to react to a promote/ Edit success initiated
   *  outside this component. */
  externalRefreshKey?: number;
  legType?: 'CL' | 'EL';
}

interface PrevShipmentAlert {
  id: string;
  ai_status?: string;
  title?: string;
}

/**
 * ShipmentDetailDrawerWithTeams component displays detailed shipment information
 * including alerts, chain of custody, and AI workflow capabilities.
 *
 * @param isOpen - Whether the drawer is open
 * @param toggleDrawer - Function to toggle drawer state
 * @param openShipmentId - ID of the shipment to display
 * @param shipmentSuccess - Callback for successful shipment operations
 * @param filterByLinkCode - Function to filter shipments by link code
 * @param isFromHome - Whether component is rendered from home page
 */
const ShipmentDetailDrawerWithTeams: React.FC<
  ShipmentDetailDrawerWithTeamsProps
> = ({
  drawerAlert,
  isOpen,
  toggleDrawer,
  openShipmentId,
  shipmentSuccess,
  filterByLinkCode,
  isFromHome = false,
  isFromActivityFeed = false,
  onLinkedShipmentClick,
  onQuoteFromShipment,
  onBookFromShipment,
  onGenerateReturnLabel,
  supportsSchedulePickup,
  onCocCommentCreated,
  onShipmentCommentsRead,
  externalRefreshKey,
  legType: propLegType,
}) => {
  const legType = propLegType || 'CL';
  const { t } = useTranslation('shipments');
  const navigate = useNavigate();
  const location = useLocation();
  const { token, decodedUserToken, checkTokenExpiry, getUserID } = useToken();
  const decodedToken = decodedUserToken();
  const userId = getUserID();
  // Use an override for linked-shipment navigation within the drawer.
  // Otherwise derive shipmentId directly from the prop so there is no
  // one-render lag that would fire API calls with a stale ID.
  const [localIdOverride, setLocalIdOverride] = useState<string | null>(null);
  const shipmentId = localIdOverride ?? openShipmentId;
  const [shipment, setShipment] = useState<SingleShipmentDataExtended>(null);

  // Reset override whenever the parent supplies a new ID (e.g. user
  // clicked a different leg).
  useEffect(() => {
    setLocalIdOverride(null);
  }, [openShipmentId]);

  // Track viewing this detail drawer, wherever in the app it was opened from.
  // Execution legs (legType 'EL') are reported by their own id, as a distinct
  // event field, rather than as a shipment.
  useEffect(() => {
    if (!isOpen || !shipmentId) return;
    if (legType === 'EL') {
      trackExecutionLegViewed(shipmentId);
    } else {
      trackShipmentViewed(shipmentId);
    }
  }, [isOpen, shipmentId, legType]);

  const hasCleanedUrlRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      hasCleanedUrlRef.current = false;
      return;
    }
    if (hasCleanedUrlRef.current) return;
    const params = new URLSearchParams(location.search);
    if (params.has('shipment_id')) {
      hasCleanedUrlRef.current = true;
      navigate(location.pathname, { replace: true });
    }
  }, [isOpen, location.pathname, location.search, navigate]);

  const companyName = decodedToken?.company_name;
  const { alerts, refetchAlerts } = useFetchShipmentAlerts(
    legType === 'CL' ? shipmentId : null,
    companyName
  );
  const {
    shipmentData,
    refetchShipment,
    loading: shipmentLoading,
  } = useFetchSingleShipment(
    legType === 'CL' ? shipmentId : null,
    false,
    'minimal'
  );

  // Phase 2: background fetch for shipment_monitor (GPS track + per-point sensor readings).
  // Fires after minimal data arrives. Warms the backend cache so the external map is fast.
  const monitorShipmentId =
    legType === 'CL' && shipmentData ? shipmentId : undefined;
  const { shipmentData: monitorData } = useFetchSingleShipment(
    monitorShipmentId,
    false,
    'full',
    legType
  );

  const { unauthorize } = useUnAuthorize();

  const isELMode = isOpen && legType === 'EL' && Boolean(shipmentId);
  const {
    detail: elDetail,
    refetchExecutionLegDetail,
    isLoading: elLoading,
  } = useFetchExecutionLegDetail(isELMode ? shipmentId : null, isELMode, false);

  useEffect(() => {
    if (!elDetail || legType !== 'EL') return;
    const formattedRes = {
      ...elDetail,
      leg_type: 'EL' as const,
    };
    setShipment(formattedRes as unknown as SingleShipmentDataExtended);

    const latestLoc = elDetail?.active_customer_leg?.latest_location;
    let newPoint = null;
    if (
      latestLoc &&
      typeof latestLoc.lat === 'number' &&
      typeof latestLoc.lng === 'number' &&
      latestLoc.lat !== 0 &&
      latestLoc.lng !== 0
    ) {
      newPoint = {
        lat: latestLoc.lat,
        lng: latestLoc.lng,
      };
    }
    setLastPoint(newPoint);
  }, [elDetail, legType]);

  const refetchData = useCallback(
    (runSpin = false, includeMonitor = true) => {
      if (legType === 'EL') {
        refetchExecutionLegDetail(runSpin);
      } else {
        refetchShipment(runSpin, includeMonitor);
      }
    },
    [legType, refetchExecutionLegDetail, refetchShipment]
  );

  useEffect(() => {
    const onPickupChanged = (e: Event) => {
      const changedId = (e as CustomEvent<{ shipmentId: string }>).detail
        ?.shipmentId;
      if (changedId && changedId === shipmentId) refetchData(false, false);
    };
    window.addEventListener(FEDEX_PICKUP_CHANGED_EVENT, onPickupChanged);
    return () =>
      window.removeEventListener(FEDEX_PICKUP_CHANGED_EVENT, onPickupChanged);
  }, [shipmentId, refetchData]);

  const [alertsArray, setAlertsArray] = useState<ShipmentAlertData[]>([]);
  const [lastPoint, setLastPoint] = useState<LatLng>(null);
  const [alertID, setAlertID] = useState<string>();
  const [startAlertsInterval, setStartAlertsInterval] = useState<boolean>(true);
  const [shareShipmentModalOpen, setShareShipmentModalOpen] =
    useState<boolean>(false);
  const [deleteShipmentModalOpen, setDeleteShipmentModalOpen] =
    useState<boolean>(false);
  const [showAiModal, setShowAiModal] = useState<boolean>(false);
  const [showEmailAiModal, setShowEmailAiModal] = useState<boolean>(false);
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [selectedAlert, setSelectedAlert] = useState<ShipmentAlertData>(null);
  const lastDetailFetchRef = useRef<number>(0);
  const isTabVisibleRef = useRef<boolean>(!document.hidden);
  const [aiTrigger, setAiTrigger] = useState(0);
  const [showDeliveredModal, setShowDeliveredModal] = useState<boolean>(false);

  useEffect(() => {
    if (!isOpen) return;

    switch (drawerAlert?.initialAction) {
      case 'share_tracking_link':
        setShareShipmentModalOpen(true);
        break;
      case 'archive_shipment':
        setDeleteShipmentModalOpen(true);
        break;
      case 'mark_as_delivered':
        setShowDeliveredModal(true);
        break;
      default:
        break;
    }
  }, [drawerAlert?.initialAction, isOpen]);

  useScrollToCocComment({
    commentId: drawerAlert?.CoC_ID,
    ready: Boolean(shipment),
    scrollToBottom: drawerAlert?.scrollToCommentsBottom,
    shipmentId,
  });

  useEffect(() => {
    setAlertsArray(alerts);
  }, [alerts]);

  useEffect(() => {
    if (shipmentData) {
      if (isOpen && shipmentId && legType === 'CL') {
        onShipmentCommentsRead?.(shipmentId);
      }

      // Preserve shipment_monitor from Phase 2 if this is a minimal refetch (monitor is empty).
      setShipment((prev) => ({
        ...shipmentData,
        sensor_summary: shipmentData.sensor_summary ?? prev?.sensor_summary,
        sensor: shipmentData.sensor ?? prev?.sensor,
        sensors: shipmentData.sensors ?? prev?.sensors,
        can_view_vitals: shipmentData.can_view_vitals ?? prev?.can_view_vitals,
        monitor_info: shipmentData.monitor_info ?? prev?.monitor_info,
        shipment_monitor: shipmentData.shipment_monitor?.length
          ? shipmentData.shipment_monitor
          : (prev?.shipment_monitor ?? []),
        shipment_polyline:
          shipmentData.shipment_polyline ?? prev?.shipment_polyline,
        legs_info: shipmentData.legs_info ?? prev?.legs_info,
      }));
      const info = Array.isArray(shipmentData?.monitor_info)
        ? shipmentData.monitor_info[0]
        : shipmentData?.monitor_info;
      let newPoint = null;
      if (
        info &&
        typeof info.latitude === 'number' &&
        typeof info.longitude === 'number' &&
        info.latitude !== 0 &&
        info.longitude !== 0
      ) {
        newPoint = {
          lat: info.latitude,
          lng: info.longitude,
        };
      }
      setLastPoint(newPoint);
    }
  }, [isOpen, legType, onShipmentCommentsRead, shipmentData, shipmentId]);

  useEffect(() => {
    if (!monitorData?.shipment_monitor?.length) return;
    if (isOpen && shipmentId && legType === 'CL') {
      onShipmentCommentsRead?.(shipmentId);
    }

    setShipment((prev) =>
      prev
        ? {
            ...prev,
            sensor_summary: monitorData.sensor_summary ?? prev.sensor_summary,
            sensor: monitorData.sensor ?? prev.sensor,
            sensors: monitorData.sensors ?? prev.sensors,
            can_view_vitals:
              monitorData.can_view_vitals ?? prev.can_view_vitals,
            monitor_info: monitorData.monitor_info ?? prev.monitor_info,
            shipment_monitor: monitorData.shipment_monitor?.length
              ? monitorData.shipment_monitor
              : prev.shipment_monitor,
            shipment_polyline:
              monitorData.shipment_polyline ?? prev.shipment_polyline,
            legs_info: monitorData.legs_info ?? prev.legs_info,
          }
        : prev
    );
  }, [isOpen, legType, monitorData, onShipmentCommentsRead, shipmentId]);

  const prevAlertsRef = useRef<PrevShipmentAlert[]>([]);

  useEffect(() => {
    if (alertsArray && alertsArray.length > 0) {
      alertsArray.forEach((newAlert) => {
        const oldAlert = prevAlertsRef.current.find(
          (a) => a.id === newAlert.id
        );
        if (oldAlert) {
          // Check for AI Call status changes
          if (
            oldAlert.ai_status === 'ai_call_in_progress' &&
            (newAlert.ai_status === 'ai_call_completed' ||
              newAlert.ai_status === 'ai_call_failed')
          ) {
            message.success(
              t('aiCallComplete', { title: newAlert.title || 'N/A' })
            );
          }
          // Check for AI Email status changes
          if (
            oldAlert.ai_status === 'ai_email_in_progress' &&
            (newAlert.ai_status === 'ai_email_completed' ||
              newAlert.ai_status === 'ai_email_failed')
          ) {
            message.success(
              t('aiEmailComplete', { title: newAlert.title || 'N/A' })
            );
          }
        }
      });
      // Store a deep enough copy for comparison
      prevAlertsRef.current = alertsArray.map((a) => ({
        id: a.id,
        ai_status: a.ai_status,
        title: a.title,
      }));
    } else {
      prevAlertsRef.current = [];
    }
  }, [alertsArray, t]);

  // Force-refetch when the parent bumps externalRefreshKey (e.g. after a
  // Quote-from-shell promote, so the Quote/Book bars disappear without a
  // manual close/reopen). Pass `false` so the refresh runs in the background
  // and avoids the full-screen loading flash that `true` triggers.
  useEffect(() => {
    if (externalRefreshKey === undefined || externalRefreshKey === 0) return;
    refetchData(false);
  }, [externalRefreshKey, refetchData]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      isTabVisibleRef.current = !document.hidden;
      if (!document.hidden && isOpen) {
        refetchAlerts();
        refetchData(false);
        lastDetailFetchRef.current = Date.now();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isOpen, refetchAlerts, refetchData]);

  const handleShareShipmentModal = () => {
    setShareShipmentModalOpen(true);
  };

  useEffect(() => {
    if (isOpen) {
      setShipment(null);
      setLastPoint(null);
    }
  }, [isOpen, shipmentId]);

  const onAIFlowSuccess = (alertData: ShipmentAlertData): void => {
    setAlertsArray((prevAlerts) =>
      prevAlerts.map((alert) =>
        alert.id === alertData.id
          ? { ...alert, ai_status: 'ai_email_in_progress' } // TODO: change to the actual ai_status or fix
          : alert
      )
    );
    fetchShipmentAlertData();
    refetchData(false);
    setShowEmailModal(false);
    setShowAiModal(false);
  };

  const fetchShipmentAlertData = useCallback(() => {
    if (!isOpen || !isTabVisibleRef.current) {
      return;
    }
    if (legType !== 'EL' && shipment?.leg_type !== 'EL') {
      refetchAlerts();
    }
    const now = Date.now();
    const shouldRefreshDetail =
      lastDetailFetchRef.current === 0 ||
      now - lastDetailFetchRef.current >= DETAIL_FORCE_REFRESH_MS;
    if (shouldRefreshDetail) {
      lastDetailFetchRef.current = now;
      console.info('[shipment_detail_poll]', {
        shipment_id: shipmentId,
        shipment_status: shipmentData?.status,
        include_monitor: true,
      });
      refetchData(false, true);
    }
  }, [
    isOpen,
    refetchAlerts,
    refetchData,
    shipmentData,
    shipmentId,
    legType,
    shipment?.leg_type,
  ]);

  // User actions must refresh both the alert banner and Chain of Custody on
  // every commit. The background poll deliberately throttles shipment-detail
  // reads, but applying that throttle here leaves rapid successive assignment
  // revisions invisible until the next poll or a full-page refresh.
  const refreshAfterAlertAction = useCallback(() => {
    if (!isOpen || !isTabVisibleRef.current) return;
    if (legType !== 'EL' && shipment?.leg_type !== 'EL') {
      refetchAlerts();
    }
    lastDetailFetchRef.current = Date.now();
    refetchData(false, false);
  }, [isOpen, legType, refetchAlerts, refetchData, shipment?.leg_type]);

  useEffect(() => {
    if (startAlertsInterval && !isZoomlogiSupport(companyName)) {
      const intervalId = setInterval(() => {
        fetchShipmentAlertData();
      }, ALERT_POLL_INTERVAL_MS);
      return () => clearInterval(intervalId);
    }
  }, [
    fetchShipmentAlertData,
    isOpen,
    shipmentId,
    startAlertsInterval,
    companyName,
  ]);

  const closeDeleteModal = () => {
    // Keep the side panel open; refresh list + detail after archive.
    shipmentSuccess();
    setDeleteShipmentModalOpen(false);
    refetchData(false);
  };

  //onUseAIClick
  const onUseAICallClick = (data: ShipmentAlertData): void => {
    setSelectedAlert(data);
    if (decodedToken?.ai_static_message === false) {
      setAiTrigger((prev) => prev + 1);
    } else {
      setAiTrigger(0);
      setShowAiModal(true);
    }
  };

  const onUseAIEmailClick = (data: ShipmentAlertData): void => {
    setSelectedAlert(data);
    if (decodedToken?.ai_email_static_message === false) {
      setShowEmailModal(true);
    } else {
      setShowEmailAiModal(true);
    }
  };
  //All relative functions of Child component ShipmentHeader.jsx
  const handleMenuClick = (e: { key: string }): void => {
    if (e.key === 'archive_shipment') {
      setDeleteShipmentModalOpen(true);
    } else if (e.key === 'edit_shipment') {
      handleEditShipmentModal();
    } else if (e.key === 'copy_shipment_link') {
      handleCopyShipmentLink();
    } else if (e.key === 'generate_return_label') {
      if (shipment && onGenerateReturnLabel) {
        onGenerateReturnLabel(shipment);
      }
    } else if (e.key === 'schedule_pickup') {
      toggleDrawer('open', 'SchedulePickup', shipmentId, undefined, legType);
    } else if (e.key === 'view_pickup_details') {
      toggleDrawer('open', 'PickupDetails', shipmentId, undefined, legType);
    } else if (e.key === 'gxp_report') {
      navigate(
        `/publish-gxp-report/${shipmentData?.temperature_gxp?.report_id}`
      );
    } else if (e.key === 'geo_fence_locations') {
      message.info('Geo-fence locations are available for sensor shipments.');
    } else if (e.key === 'mark_as_delivered') {
      setShowDeliveredModal(true);
    }
  };

  // Copy a shareable link to this shipment, scoped to the current frontend host
  const handleCopyShipmentLink = () => {
    const shipmentLink = `${window.location.origin}/shipment?shipment_id=${shipmentId}`;
    navigator.clipboard
      .writeText(shipmentLink)
      .then(() => {
        message.success('Shipment link copied to clipboard');
      })
      .catch(() => {
        message.error('Failed to copy shipment link');
      });
  };

  //Mark as delivered shipment
  const handleMarkAsDevliered = () => {
    // Keep the side panel open; refresh list + detail after mark delivered.
    shipmentSuccess();
    refetchData(false);
  };

  // Edit Shipment
  const handleEditShipmentModal = () => {
    toggleDrawer('open', 'Edit', shipmentId);
  };

  const shipmentLegs = Array.isArray(shipment?.legs_records)
    ? shipment.legs_records
    : [];
  const activeExecutionLeg = shipment?.active_execution_leg;
  const activeExecutionLegId =
    typeof activeExecutionLeg === 'string'
      ? activeExecutionLeg
      : activeExecutionLeg?.id;
  const activeExecutionLegIndex = shipmentLegs.findIndex(
    (leg) => leg.id === activeExecutionLegId
  );
  const activeExecutionLegRank =
    activeExecutionLegIndex >= 0
      ? shipmentLegs[activeExecutionLegIndex]?.rank ||
        activeExecutionLegIndex + 1
      : null;
  const legContextLabel =
    shipment?.leg_type === 'EL'
      ? 'Execution leg'
      : shipment?.leg_type === 'CL' && activeExecutionLegRank
        ? `Leg ${activeExecutionLegRank} of ${shipment.leg_count || shipmentLegs.length}`
        : undefined;

  // Breadcrumb stack of panels opened to reach this one (oldest first).
  // The back arrow always targets the immediate previous entry, popping it
  // off so the panel before that remains reachable on the next back-click.
  const navStack = drawerAlert?.navStack ?? [];
  const backEntry =
    navStack.length > 0 ? navStack[navStack.length - 1] : undefined;
  const onBackToParent = backEntry
    ? () => {
        const poppedStack = navStack.slice(0, -1);
        toggleDrawer(
          'open',
          'Detail',
          backEntry.id,
          poppedStack.length ? { navStack: poppedStack } : undefined,
          backEntry.legType
        );
      }
    : undefined;

  // Scoped to this panel, not the app-global spinner: while there's no
  // shipment to show yet, render a local spinner instead of the previous
  // half-rendered header/sections. Only the initial load, not background
  // polling refetches (those don't clear `shipment`).
  const isDetailLoading =
    !shipment && (legType === 'EL' ? elLoading : shipmentLoading);

  return (
    <>
      <Flex vertical={true} gap={20}>
        {isDetailLoading ? (
          <Flex justify='center' style={{ padding: '80px 0' }}>
            <Spin size='large' />
          </Flex>
        ) : (
          <>
            <ShipmentHeader
              userId={userId}
              handleShareShipmentModal={handleShareShipmentModal}
              checkTokenExpiry={checkTokenExpiry}
              alertsArray={alertsArray}
              referenceNumber={
                legType === 'CL' ? shipment?.reference_number : shipment?.mbl
              }
              legContextLabel={legContextLabel}
              shipmentId={shipmentId}
              hasNotification={shipment?.has_notification ? true : false}
              handleMenuClick={handleMenuClick}
              supportsSchedulePickup={supportsSchedulePickup}
              toggleDrawer={toggleDrawer}
              unauthorize={unauthorize}
              isFromHome={isFromHome}
              teams={shipment?.teams ? shipment?.teams : []}
              initialAction={drawerAlert?.initialAction}
              isShell={canQuoteOrBookShell(shipment)}
              onQuoteShell={
                shipment && onQuoteFromShipment && canQuoteOrBookShell(shipment)
                  ? () => onQuoteFromShipment(shipment)
                  : undefined
              }
              onBookShell={
                shipment && onBookFromShipment && canQuoteOrBookShell(shipment)
                  ? () => onBookFromShipment(shipment)
                  : undefined
              }
              shipmentData={shipment}
              legType={legType ?? shipment?.leg_type}
              legMode={
                (legType ?? shipment?.leg_type) === 'EL'
                  ? (
                      shipment as unknown as {
                        mode?: ExecutionLegDetail['mode'];
                      }
                    )?.mode
                  : undefined
              }
              backReferenceLabel={backEntry?.reference}
              onBackToParent={onBackToParent}
              isFromActivityFeed={isFromActivityFeed}
            />
            {!isZoomlogiSupport(companyName) &&
              legType !== 'EL' &&
              shipment?.leg_type !== 'EL' && (
                <ShipmentAlerts
                  shipmentSuccess={shipmentSuccess}
                  checkTokenExpiry={checkTokenExpiry}
                  alertID={alertID}
                  setStartAlertsInterval={setStartAlertsInterval}
                  setAlertID={setAlertID}
                  alertsArray={alertsArray}
                  OnUseAICallClick={onUseAICallClick}
                  OnUseAIEmailClick={onUseAIEmailClick}
                  shipment={shipment}
                  onAlertSucccess={refreshAfterAlertAction}
                  shipmentId={shipmentId}
                  unauthorize={unauthorize}
                  handleMarkAsDevliered={handleMarkAsDevliered}
                  toggleDrawer={toggleDrawer}
                />
              )}
            <ShipmentDetail
              shipment={shipment}
              toggleDrawer={toggleDrawer}
              setShipmentId={(id: string) => setLocalIdOverride(id)}
              lastPoint={lastPoint}
              filterByLinkCode={filterByLinkCode}
              onLinkedShipmentClick={onLinkedShipmentClick}
              navStack={navStack}
            />

            <CoC_CT
              toggleDrawer={toggleDrawer}
              fetchSingleShipmentData={refetchShipment}
              shipment={shipment}
              shipmentId={legType === 'CL' ? shipmentId : null}
              onCommentCreated={onCocCommentCreated}
              legType={legType ?? shipment?.leg_type}
            />
          </>
        )}
      </Flex>

      {shareShipmentModalOpen && (
        <CustomModalWithoutButton
          modalOpen={shareShipmentModalOpen}
          setModalOpen={setShareShipmentModalOpen}
          modalTitle={t('shareShipment')}
          modalText=''
          modalSize={524}
        >
          <ShareShipment shipmentId={shipmentId} token={token} />
        </CustomModalWithoutButton>
      )}

      {deleteShipmentModalOpen && (
        <ArchiveShipmentModal
          checkTokenExpiry={checkTokenExpiry}
          shipmentId={shipmentId}
          closeDeleteModal={closeDeleteModal}
          deleteShipmentModalOpen={deleteShipmentModalOpen}
          setDeleteShipmentModalOpen={setDeleteShipmentModalOpen}
          unauthorize={unauthorize}
        />
      )}

      {showDeliveredModal && (
        <MarkAsDeliveredModal
          shipmentData={shipment}
          type={'single'}
          open={showDeliveredModal}
          onCancel={() => setShowDeliveredModal(false)}
          onSuccess={handleMarkAsDevliered}
        />
      )}

      <AIWorkFlow
        selectedAlert={selectedAlert}
        shipment={shipment}
        showAiModal={showAiModal}
        setShowAiModal={setShowAiModal}
        aiTrigger={aiTrigger}
        showEmailModal={showEmailModal}
        setShowEmailAiModal={setShowEmailAiModal}
        showEmailAiModal={showEmailAiModal}
        setShowEmailModal={setShowEmailModal}
        onAIFlowSuccess={onAIFlowSuccess}
      />
    </>
  );
};

export default ShipmentDetailDrawerWithTeams;
